// Ported from nenda-autoplayer's src/lib/state/autoplayerMachine.ts. The
// state chart (states/on/actions/guards/actors/delays) is verbatim - not one
// transition, guard, or action wiring changed. What changed:
//
//   1. Types narrowed to Foyer's own interfaces, all already defined and
//      tested in earlier commits this session: `NendaApiClient` ->
//      `ScheduleClient` (src/tv/signage/foyerScheduleClient.ts),
//      `NendaLogClient` -> `LogClient` (src/tv/signage/webLogClient.ts),
//      `DeviceController` -> `WebDeviceController`
//      (src/tv/signage/webDeviceController.ts).
//   2. Dropped the auto-generated `@xstate-layout` visualization comment
//      upstream embeds above `.createMachine({...})` - a base64 blob tied
//      to the exact upstream structure, stale the moment any delta lands,
//      and not functional code.
//   3. NO module-level auto-instantiated default export. Upstream ends with
//      `export default autoplayerMachine(new NendaApiClient(), new
//      NendaLogClient(), DeviceController.getInstance())` - a bare
//      singleton built with a no-args client. Foyer's `FoyerScheduleClient`
//      needs a `getCurrentTimeline` callback injected per mount (it reads
//      SignageEngineSurface's own polled timeline, not a network call - see
//      that file's own doc comment), so there is no single correct
//      module-level instance to export. `autoplayerMachine` is exported as
//      the factory function itself; the caller (SignageEngineSurface)
//      constructs its own `FoyerScheduleClient`/`WebLogClient`/
//      `WebDeviceController.getInstance()` and calls this per screen.
import {
  setup,
  assign,
  fromPromise,
  raise,
  SnapshotFrom,
  ActorRefFrom,
  and,
  sendTo,
} from "xstate";
import {
  AssetCompleteEvent,
  AssetCompleteStatus,
  AutoplayerMachineEvent,
  FetchAdsDone,
  FetchScheduleDone,
  RedBeeAuthDone,
} from "./events";
import * as actions from "./actions";
import { Session } from "../types/Session";
import type { ScheduleClient } from "../../signage/foyerScheduleClient";
import { ScheduleType } from "../types/Schedule";
import {
  areAdsAllowed,
  hasAds,
  hasContentChanged,
  hasNextAsset,
  hasSignage,
  isSignageAllowed,
  shouldPlayAd,
  shouldPlaySignage,
} from "./guards";
import { LogLevel } from "../types/LogEntry";
import type { LogClient } from "../../signage/webLogClient";
import type WebDeviceController from "../../signage/webDeviceController";
import { playbackCircuitBreakerMachine } from "../playback/playbackCircuitBreakerMachine";
import type { AutoplayerMachineContext } from "./context";

export type { AutoplayerMachineContext, PlaybackContextType } from "./context";

type BreakerRef = ActorRefFrom<typeof playbackCircuitBreakerMachine>;

export const autoplayerMachine = (
  scheduleClient: ScheduleClient,
  logClient: LogClient,
  deviceController: WebDeviceController
) =>
  setup({
    types: {
      context: {} as AutoplayerMachineContext,
      input: {} as {
        session: Session;
      },
      events: {} as AutoplayerMachineEvent,
    },
    guards: {
      hasNextAsset: ({ context }, params: { type: ScheduleType }) =>
        hasNextAsset(context, params.type),
      shouldPlayAd: ({ context }) => shouldPlayAd(context),
      shouldPlaySignage: ({ context }) => shouldPlaySignage(context),
      isSignageAllowed: ({ context }) => isSignageAllowed(context),
      areAdsAllowed: ({ context }) => areAdsAllowed(context),
      hasAds: ({ context }) => hasAds(context),
      hasSignage: ({ context }) => hasSignage(context),
      hasContentChanged: ({ context }) => hasContentChanged(context),
      breakerOpen: ({ context }) =>
        context.playback.circuitBreakerState === "open",
      breakerHalfOpen: ({ context }) =>
        context.playback.circuitBreakerState === "half-opened",
      breakerClosed: ({ context }) =>
        context.playback.circuitBreakerState === "closed",
    },
    delays: {
      checkScheduleInterval: 1000 / 2,
      sendPingInterval: 5 * 60 * 1000,
      reconnectDelay: ({ context }: { context: AutoplayerMachineContext }) =>
        Math.min(60000, 5000 * 2 ** context.reconnectAttempts),
      // Poll every 30s normally; on consecutive fetch errors back off
      // exponentially up to 5 min so a backend blip isn't hammered.
      scheduleFetchDelay: ({
        context,
      }: {
        context: AutoplayerMachineContext;
      }) =>
        context.scheduleFetchFailures === 0
          ? 30 * 1000
          : Math.min(
              5 * 60 * 1000,
              30 * 1000 * 2 ** context.scheduleFetchFailures
            ),
    },
    actions: {
      evaluateSchedule: assign(({ context }) =>
        actions.evaluateSchedule(context)
      ),
      registerAdView: ({ context }, params: { event: AssetCompleteEvent }) => {
        if (params.event.status === AssetCompleteStatus.SUCCESS) {
          actions.registerAdView(context, scheduleClient);
          actions.logAdView(logClient, context);
        }
        if (params.event.status === AssetCompleteStatus.ERROR) {
          actions.logAdPlaybackError(logClient, context, params.event.error);
        }
      },
      playNextAsset: assign(
        (
          { context },
          params: {
            event?: AssetCompleteEvent;
            type: ScheduleType;
          }
        ) => actions.playNextAsset(context, params.type)
      ),
      restartPlaylist: assign(({ context }) =>
        actions.restartContentPlaylist(context)
      ),
      recordAssetStartTime: assign(({ context }) =>
        actions.recordAssetStartTime(context)
      ),
      resetTimes: assign(({ context }) => actions.resetTimes(context)),
      setAdPlaylist: assign(({ context, event }) =>
        actions.setAdPlaylist(context, event as FetchAdsDone)
      ),
      setSignageContext: assign(({ context }) =>
        actions.setSignageContext(context)
      ),
      storeSchedule: assign({
        schedule: ({ event }) => (event as FetchScheduleDone).output,
      }),
      storeRedBeeSession: assign({
        redBeeSessionToken: ({ event }) =>
          (event as RedBeeAuthDone).output.sessionToken,
      }),
      changeContent: assign(({ context }) => actions.changeContent(context)),
      calculatePlaybackTimes: assign(({ context }) =>
        actions.calculatePlaybackTimes(context)
      ),
      runCommands: ({ context }) =>
        actions.runCommands(context, deviceController, logClient),
      logPlay: ({ context }, params: { type: ScheduleType }) =>
        actions.logPlay(logClient, context, params.type),
      logContentPlaylistPlay: ({ context }) =>
        actions.logContentPlaylistPlay(logClient, context),
      logPing: ({ context, self }) => {
        const snapshot = self.getSnapshot();
        actions.logPing(logClient, context, snapshot.value);
      },
      logContentAssetComplete: ({ context, event }) =>
        actions.logContentAssetComplete(
          logClient,
          context,
          event as AssetCompleteEvent
        ),
      log: (
        { context },
        params: {
          component: string;
          message?: string;
          level?: LogLevel;
          asset?: import("../types/Schedule").MediaAsset;
        }
      ) =>
        actions.log({
          client: logClient,
          context,
          component: params.component,
          message: params.message,
          level: params.level,
          asset: params.asset,
        }),
      breakerFail: sendTo(
        ({ context }) => context.breakerRef as BreakerRef,
        () => ({ type: "FAIL" })
      ),
      breakerSuccess: sendTo(
        ({ context }) => context.breakerRef as BreakerRef,
        () => ({ type: "SUCCESS" })
      ),
    },
    actors: {
      fetchSchedule: fromPromise(
        async ({ input: { session } }: { input: AutoplayerMachineContext }) => {
          return await scheduleClient.getScreenSchedule(session);
        }
      ),
      fetchAds: fromPromise(
        async ({ input: { session } }: { input: AutoplayerMachineContext }) => {
          return await actions.getAdSlot(scheduleClient, session);
        }
      ),
      authenticateRedBeeUser: fromPromise(
        async ({ input: { session } }: { input: AutoplayerMachineContext }) => {
          return await actions.authenticateRedBeeUser(scheduleClient, session);
        }
      ),
      checkConnectivity: fromPromise(async () =>
        actions.checkConnectivity(scheduleClient)
      ),
    },
  }).createMachine({
    id: "autoplayer",
    initial: "Initializing",
    context: ({ input, spawn }): AutoplayerMachineContext => ({
      breakerRef: spawn(playbackCircuitBreakerMachine),
      session: input.session,
      reconnectAttempts: 0,
      scheduleFetchFailures: 0,
      playback: {
        circuitBreakerState: "closed",
        content: {
          currentIndex: 0,
          currentContent: undefined,
        },
        ad: {
          currentIndex: 0,
        },
        signage: {
          currentIndex: 0,
          currentSignagePlaylist: [],
        },
      },
    }),
    states: {
      Offline: {
        initial: "Idle",
        states: {
          Idle: {
            on: {
              NETWORK_RESTORED: "Reconnecting",
            },
          },
          Reconnecting: {
            initial: "Idle",
            states: {
              Idle: {
                entry: assign({
                  reconnectAttempts: ({ context }) =>
                    context.reconnectAttempts + 1,
                }),
                after: {
                  reconnectDelay: "Check",
                },
              },
              Check: {
                invoke: {
                  id: "checkConnectivity",
                  src: "checkConnectivity",
                  onDone: {
                    target: "#autoplayer.Initializing",
                    actions: [
                      assign({ reconnectAttempts: () => 0 }),
                      {
                        type: "log",
                        params: {
                          component: "checkConnectivity",
                          message: "Connection restored",
                        },
                      },
                    ],
                  },
                  onError: {
                    target: "Idle",
                    actions: () => console.error("Connectivity check failed:"),
                  },
                },
              },
            },
          },
        },
      },
      Error: {},
      Initializing: {
        initial: "Authenticate RedBee user",
        entry: {
          type: "log",
          params: { component: "Initializing" },
        },
        states: {
          "Authenticate RedBee user": {
            invoke: {
              id: "authenticateRedBeeUser",
              src: "authenticateRedBeeUser",
              input: ({ context }) => context,
              onDone: {
                actions: [
                  "storeRedBeeSession",
                  {
                    type: "log",
                    params: {
                      component: "Initializing",
                      message: "Authenticate RedBee user: Success",
                    },
                  },
                ],
                target: "Fetch schedule",
              },
              onError: {
                actions: [
                  ({ event }: { event: { error: unknown } }) => {
                    const err = event.error;
                    const msg =
                      err instanceof Error ? err.message : String(err);
                    if (
                      typeof (
                        window as unknown as { dbg?: (s: string) => void }
                      ).dbg === "function"
                    ) {
                      (window as unknown as { dbg: (s: string) => void }).dbg(
                        "[auth-err] continuing without token: " +
                          msg.slice(0, 80)
                      );
                    }
                  },
                  {
                    type: "log",
                    params: {
                      component: "Initializing",
                      message:
                        "Authenticate RedBee user: Error — continuing without token",
                      level: LogLevel.WARNING,
                    },
                  },
                ],
                target: "Fetch schedule",
              },
            },
          },
          "Fetch schedule": {
            invoke: {
              id: "initSchedule",
              src: "fetchSchedule",
              input: ({ context }) => context,
              onDone: {
                actions: [
                  "storeSchedule",
                  "changeContent",
                  () => {
                    if (
                      typeof (
                        window as unknown as { dbg?: (s: string) => void }
                      ).dbg === "function"
                    )
                      (window as unknown as { dbg: (s: string) => void }).dbg(
                        "[sched-done] schedule stored, going to Scheduled player"
                      );
                  },
                  {
                    type: "log",
                    params: {
                      component: "Initializing",
                      message: "Fetch schedule: Success",
                    },
                  },
                ],
                target: "#autoplayer.Scheduled player",
              },
              onError: {
                actions: [
                  ({ event }: { event: { error: unknown } }) => {
                    const msg =
                      event.error instanceof Error
                        ? event.error.message
                        : String(event.error);
                    if (
                      typeof (
                        window as unknown as { dbg?: (s: string) => void }
                      ).dbg === "function"
                    )
                      (window as unknown as { dbg: (s: string) => void }).dbg(
                        "[sched-err] " + msg.slice(0, 150)
                      );
                  },
                  {
                    type: "log",
                    params: {
                      component: "Initializing",
                      message: "Fetch schedule: Error",
                      level: LogLevel.SEVERE,
                    },
                  },
                ],
                target: "Error",
              },
            },
          },
          Error: {},
        },
      },
      "Scheduled player": {
        type: "parallel",
        states: {
          Playback: {
            initial: "Content",
            states: {
              Content: {
                initial: "Playing",
                states: {
                  Playing: {
                    entry: [
                      "recordAssetStartTime",
                      {
                        type: "logPlay",
                        params: { type: ScheduleType.Content },
                      },
                    ],
                  },
                  "Switching content": {
                    entry: ["resetTimes", "logContentPlaylistPlay"],
                    always: [
                      {
                        target: "Playing",
                      },
                    ],
                  },
                  "Paused by circuit breaker": {
                    entry: [
                      "resetTimes",
                      {
                        type: "log",
                        params: {
                          level: LogLevel.SEVERE,
                          component: "Playback Circuit Breaker",
                          message: "Breaker open → pausing playback",
                        },
                      },
                    ],
                  },
                },
                on: {
                  ASSET_COMPLETE: [
                    {
                      guard: ({ event }) =>
                        event.status === AssetCompleteStatus.ERROR,
                      actions: [
                        "logContentAssetComplete",
                        "breakerFail",
                        "resetTimes",
                        raise({ type: "ADVANCE_CONTENT" }),
                      ],
                      target: "Content",
                    },
                    {
                      guard: ({ event }) =>
                        event.status === AssetCompleteStatus.SUCCESS,
                      actions: [
                        "logContentAssetComplete",
                        "breakerSuccess",
                        "resetTimes",
                        raise({ type: "ADVANCE_CONTENT" }),
                      ],
                      target: "Content",
                    },
                  ],
                  ADVANCE_CONTENT: [
                    {
                      guard: "breakerOpen",
                      target: "Content.Paused by circuit breaker",
                    },
                    {
                      guard: {
                        type: "hasNextAsset",
                        params: { type: ScheduleType.Content },
                      },
                      actions: [
                        {
                          type: "playNextAsset",
                          params: () => ({ type: ScheduleType.Content }),
                        },
                        "resetTimes",
                      ],
                      target: "Content",
                    },
                    {
                      actions: ["restartPlaylist", "resetTimes"],
                      target: "Content",
                    },
                  ],
                  PLAY_AD: {
                    actions: "calculatePlaybackTimes",
                    target: "Ad",
                  },
                  PLAY_SIGNAGE: {
                    actions: "calculatePlaybackTimes",
                    target: "Signage",
                  },
                  CONTENT_CHANGED: {
                    target: "Content.Switching content",
                  },
                  PLAYBACK_CIRCUITBREAKER_OPENED: {
                    actions: [
                      {
                        type: "log",
                        params: {
                          component: "Playback Circuit Breaker",
                          message:
                            "Circuit breaker opened - stopping playback attempts",
                          level: LogLevel.SEVERE,
                        },
                      },
                      assign(({ context }) => ({
                        playback: {
                          ...context.playback,
                          circuitBreakerState: "open" as const,
                        },
                      })),
                    ],
                  },
                  PLAYBACK_CIRCUITBREAKER_HALF_OPENED: {
                    actions: [
                      {
                        type: "log",
                        params: {
                          component: "Playback Circuit Breaker",
                          message:
                            "Circuit breaker half-opened - attempting recovery",
                          level: LogLevel.WARNING,
                        },
                      },
                      assign(({ context }) => ({
                        playback: {
                          ...context.playback,
                          circuitBreakerState: "half-opened" as const,
                        },
                      })),
                      raise({ type: "ADVANCE_CONTENT" }),
                    ],
                  },
                  PLAYBACK_CIRCUITBREAKER_CLOSED: {
                    actions: [
                      {
                        type: "log",
                        params: {
                          component: "Playback Circuit Breaker",
                          message:
                            "Circuit breaker closed - resuming normal playback",
                          level: LogLevel.INFO,
                        },
                      },
                      assign(({ context }) => ({
                        playback: {
                          ...context.playback,
                          circuitBreakerState: "closed" as const,
                        },
                      })),
                    ],
                  },
                },
              },
              Ad: {
                initial: "Playing",
                states: {
                  Playing: {
                    entry: {
                      type: "logPlay",
                      params: { type: ScheduleType.Ad },
                    },
                  },
                  "Ad slot complete": {
                    always: [
                      {
                        target: "#autoplayer.Scheduled player.Playback.Content",
                      },
                    ],
                  },
                },
                on: {
                  ASSET_COMPLETE: [
                    {
                      guard: {
                        type: "hasNextAsset",
                        params: { type: ScheduleType.Ad },
                      },
                      actions: [
                        {
                          type: "registerAdView",
                          params: ({ event }) => {
                            return { event: event };
                          },
                        },
                        {
                          type: "playNextAsset",
                          params: ({ event }) => {
                            return { event: event, type: ScheduleType.Ad };
                          },
                        },
                      ],
                      target: "Ad",
                    },
                    {
                      actions: {
                        type: "registerAdView",
                        params: ({ event }) => {
                          return { event: event };
                        },
                      },
                      target: "Ad.Ad slot complete",
                    },
                  ],
                },
              },
              Signage: {
                initial: "Playing",
                states: {
                  Playing: {
                    entry: {
                      type: "logPlay",
                      params: { type: ScheduleType.Signage },
                    },
                  },
                  "Signage complete": {
                    always: [
                      {
                        target: "#autoplayer.Scheduled player.Playback.Content",
                      },
                    ],
                  },
                },
                on: {
                  ASSET_COMPLETE: [
                    {
                      guard: {
                        type: "hasNextAsset",
                        params: { type: ScheduleType.Signage },
                      },
                      target: "Signage",
                      actions: [
                        {
                          type: "playNextAsset",
                          params: ({ event }) => {
                            return { event: event, type: ScheduleType.Signage };
                          },
                        },
                      ],
                    },
                    {
                      target: "Signage.Signage complete",
                    },
                  ],
                },
              },
            },
          },
          "Update schedule": {
            initial: "Idle",
            states: {
              Idle: {
                after: {
                  scheduleFetchDelay: "Fetching",
                },
              },
              Fetching: {
                invoke: {
                  id: "pollScheduleUpdate",
                  src: "fetchSchedule",
                  input: ({ context }) => context,
                  onDone: {
                    actions: [
                      "storeSchedule",
                      "runCommands",
                      assign({ scheduleFetchFailures: () => 0 }),
                    ],
                    target: "Idle",
                  },
                  onError: {
                    actions: [
                      {
                        type: "log",
                        params: {
                          component: "Update schedule",
                          message: "Error fetching schedule",
                          level: LogLevel.SEVERE,
                        },
                      },
                      assign({
                        scheduleFetchFailures: ({ context }) =>
                          context.scheduleFetchFailures + 1,
                      }),
                    ],
                    target: "Idle",
                  },
                },
              },
            },
          },
          "Check schedule": {
            initial: "Idle",
            states: {
              Idle: {
                after: {
                  checkScheduleInterval: "Evaluating",
                },
              },
              Evaluating: {
                entry: "evaluateSchedule",
                after: {
                  checkScheduleInterval: [
                    {
                      guard: and(["areAdsAllowed", "shouldPlayAd"]),
                      target: "Fetching ads",
                    },
                    {
                      guard: and(["isSignageAllowed", "shouldPlaySignage"]),
                      actions: "setSignageContext",
                      target: "Initialize signage play",
                    },
                    {
                      target: "Check for content change",
                    },
                  ],
                },
              },
              "Check for content change": {
                always: [
                  {
                    guard: "hasContentChanged",
                    actions: [
                      "changeContent",
                      raise({ type: "CONTENT_CHANGED" }),
                    ],
                    target: "Idle",
                  },
                  {
                    target: "Idle",
                  },
                ],
              },
              "Fetching ads": {
                invoke: {
                  id: "fetchAds",
                  src: "fetchAds",
                  input: ({ context }) => context,
                  onDone: [
                    {
                      actions: [
                        "setAdPlaylist",
                        {
                          type: "log",
                          params: {
                            component: "Fetching ads",
                            message: "Fetch ads: Success",
                          },
                        },
                      ],
                      target: "Initialize ad play",
                    },
                  ],
                  onError: {
                    actions: {
                      type: "log",
                      params: {
                        component: "Check schedule",
                        message: "Error fetching ads",
                        level: LogLevel.SEVERE,
                      },
                    },
                    target: "Idle",
                  },
                },
              },
              "Initialize ad play": {
                always: [
                  {
                    guard: "hasAds",
                    actions: raise({ type: "PLAY_AD" }),
                    target: "Idle",
                  },
                  {
                    actions: {
                      type: "log",
                      params: {
                        component: "Fetching ads",
                        message: "No ads to play",
                      },
                    },
                    target: "Idle",
                  },
                ],
              },
              "Initialize signage play": {
                always: [
                  {
                    guard: "hasSignage",
                    actions: raise({ type: "PLAY_SIGNAGE" }),
                    target: "Idle",
                  },
                ],
              },
            },
          },
          "Send ping": {
            initial: "Sending",
            states: {
              Idle: {
                after: {
                  sendPingInterval: "Sending",
                },
              },
              Sending: {
                entry: "logPing",
                always: "Idle",
              },
            },
          },
          "Debug view": {
            initial: "off",
            states: {
              off: {
                on: {
                  TOGGLE_DEBUG: "on",
                },
              },
              on: {
                on: {
                  TOGGLE_DEBUG: "off",
                },
              },
            },
          },
        },
      },
    },
    on: {
      NO_NETWORK: "#autoplayer.Offline",
      LOG: {
        actions: {
          type: "log",
          params: ({ event }) => {
            return {
              component: event.payload.component,
              message: event.payload.message,
              level: event.payload.level,
              asset: event.payload.asset,
            };
          },
        },
      },
    },
  });

export type Snapshot = SnapshotFrom<ReturnType<typeof autoplayerMachine>>;
export type AutoplayerMachineActorRef = ActorRefFrom<
  ReturnType<typeof autoplayerMachine>
>;
